import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { EditNoteContent } from './edit-note-content';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import {
  SCREEN_PADDING,
  UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH,
} from '@/styles';
import { StyleSheet, View } from 'react-native';
import { UpdateOrderGuideItemSkuList } from './update-order-guide-item-sku-list';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  itemMetadata: ItemListOptionMetadata;
  onClose: () => void;
  onSave: (
    orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment,
    nextValue: string | null,
    persistNotes: boolean,
  ) => Promise<void>;
}

enum NoteSheetRoute {
  EditNote = 'EDIT_NOTE',
  SelectSku = 'SELECT_SKU',
}

export const EditNoteActionSheet: React.FC<Props> = ({
  itemMetadata,
  onClose,
  onSave,
}) => {
  const { isSmallLayout } = useLayoutContext();

  const getOrderGuideItem = useGetOrderGuideItem();

  const [orderGuideItem, setOrderGuideItem] =
    useState<ItemsContext_OrderGuideItemFieldsFragment | null>(() =>
      itemMetadata.skus.length <= 1 ? getOrderGuideItem(itemMetadata) : null,
    );
  const renderSingleSku = itemMetadata.skus.length <= 1;

  return (
    <CombinedActionSheet
      contentStyle={styles.content}
      hasStickyHeader={false}
      initialRoute={
        renderSingleSku ? NoteSheetRoute.EditNote : NoteSheetRoute.SelectSku
      }
      modalStyle={isSmallLayout ? null : styles.modalLarge}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo }) => {
        if (routeKey === NoteSheetRoute.EditNote) {
          return orderGuideItem ? (
            <View
              style={isSmallLayout ? styles.paddingReset : styles.paddingLarge}
            >
              <EditNoteContent
                onClose={onClose}
                onSave={async (
                  nextValue: string | null,
                  persistNotes: boolean,
                ) => {
                  await onSave(orderGuideItem, nextValue, persistNotes);
                }}
                orderGuideItem={orderGuideItem}
              />
            </View>
          ) : null;
        }
        return (
          <UpdateOrderGuideItemSkuList
            itemMetadata={itemMetadata}
            onSelect={(option) => {
              const nextOrderGuideItem = getOrderGuideItem(
                option.metadata!.sku,
              );
              if (nextOrderGuideItem) {
                setOrderGuideItem(nextOrderGuideItem);
                jumpTo(NoteSheetRoute.EditNote);
              }
            }}
          />
        );
      }}
      routes={{
        [NoteSheetRoute.EditNote]: {
          previousKey: renderSingleSku ? null : NoteSheetRoute.SelectSku,
          showSmallLayoutBackButton: !renderSingleSku,
          showSmallLayoutTitle: true,
          title: i18n.t('order_guide_note.title'),
        },
        [NoteSheetRoute.SelectSku]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('order_guide_note.title'),
        },
      }}
      titleStyle={styles.modalTitle}
    />
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 0,
  },
  modalLarge: {
    maxWidth: UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH,
  },
  modalTitle: {
    marginLeft: 24,
  },
  paddingLarge: {
    paddingHorizontal: 60,
  },
  paddingReset: {
    paddingHorizontal: SCREEN_PADDING,
  },
});
